import React, { useState, useEffect, useMemo } from 'react';
import axios from '../../utils/api';
import { PREVIEW_SANDBOX, buildBaseTag, escapeHtml, openSandboxedPreviewWindow } from '../../utils/sandboxedPreview';
import './BackgroundTester.css';

// Фон (HTML/CSS/скрипты из пресетов) рендерится в iframe sandbox="allow-scripts" без allow-same-origin.
// Правила раскладки слоёв совпадают с прежними .bg-tester__preview-frame > div.
const buildBackgroundPreviewDocument = (previewHtml: string): string => `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
${buildBaseTag()}
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
html, body { width: 100%; height: 100%; overflow: hidden; background: #ffffff; }
#bg-preview-root { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: #ffffff; }
#bg-preview-root > div { position: absolute !important; top: 0 !important; left: 0 !important; width: 100% !important; height: 100% !important; }
#bg-preview-root > div:first-child { z-index: 1 !important; }
#bg-preview-root > div:nth-child(2) { z-index: 2 !important; }
</style>
</head>
<body><div id="bg-preview-root">${previewHtml}</div></body>
</html>`;

const buildBackgroundFullscreenDocument = (previewHtml: string, label: string): string => `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
${buildBaseTag()}
<title>Background Preview</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { min-height: 100vh; position: relative; background: #fff; }
</style>
</head>
<body>
  ${previewHtml}
  <div style="position: relative; z-index: 10; padding: 50px; text-align: center;">
    <div style="background: rgba(255,255,255,0.9); padding: 30px; border-radius: 12px; display: inline-block;">
      <h1 style="margin: 0 0 10px; color: #333;">Background Preview</h1>
      <p style="margin: 0; color: #666;">${escapeHtml(label)}</p>
    </div>
  </div>
</body>
</html>`;

interface BackgroundPreset {
  id: number;
  name: string;
  type: 'static' | 'dynamic';
  subtype: string;
  config: Record<string, any>;
  color_scheme: string;
  performance_weight: number;
  is_active: boolean;
  preview_url?: string;
  description?: string;
}

interface CuratedBackground {
  id: number;
  name: string;
  url: string;
  type: string;
  color_scheme: string;
  seamless: boolean;
  tags?: string[];
}

export const BackgroundTester: React.FC = () => {
  const [regenerating, setRegenerating] = useState(false);
  const [thumbnailNotice, setThumbnailNotice] = useState('');
  const regenerateThumbnails = async () => {
    setRegenerating(true); setThumbnailNotice('');
    try {
      const response = await axios.post('/api/admin/component-tester/background-thumbnails/regenerate');
      const data = response.data;
      if (!data.success) throw new Error('failed');
      setThumbnailNotice(`Обновлено ${data.updated} из ${data.total}. Ошибок: ${data.failed}.`);
      await loadData();
    } catch { setThumbnailNotice('Не удалось обновить превью фонов. Повторите позже.'); }
    finally { setRegenerating(false); }
  };
  // Данные
  const [presets, setPresets] = useState<BackgroundPreset[]>([]);
  const [curatedBackgrounds, setCuratedBackgrounds] = useState<CuratedBackground[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Настройки
  const [selectedMode, setSelectedMode] = useState<'dynamic' | 'static'>('static');
  const [selectedPresetId, setSelectedPresetId] = useState<number | null>(null);
  const [selectedCuratedId, setSelectedCuratedId] = useState<number | null>(null);
  const [colorScheme, setColorScheme] = useState<string>('any');
  const [performanceFilter, setPerformanceFilter] = useState<number>(3);
  const [overlayOpacity, setOverlayOpacity] = useState<number>(0); // 0 = картинка видна, 1 = белый фон
  
  // Preview
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewInfo, setPreviewInfo] = useState<any>(null);

  // Загрузка данных при монтировании
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [presetsRes, curatedRes] = await Promise.all([
        axios.get('/api/admin/component-tester/background-presets'),
        axios.get('/api/admin/component-tester/curated-backgrounds')
      ]);
      
      const presetsData = presetsRes.data;
      const curatedData = curatedRes.data;
      
      if (presetsData.success) {
        setPresets(presetsData.presets || []);
      }
      if (curatedData.success) {
        setCuratedBackgrounds(curatedData.backgrounds || []);
      }
      
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Генерация превью
  const generatePreview = async (presetId?: number, curatedId?: number) => {
    try {
      setPreviewLoading(true);
      
      const body: any = {
        mode: selectedMode,
        colorScheme,
        performancePriority: performanceFilter === 1 ? 'lightweight' : performanceFilter === 2 ? 'balanced' : 'visual_first',
        overlayOpacity
      };
      
      if (selectedMode === 'dynamic' && presetId) {
        body.presetId = presetId;
      }
      if (selectedMode === 'static' && curatedId) {
        body.curatedId = curatedId;
      }
      
      const res = await axios.post('/api/admin/component-tester/background-preview', body);
      
      const data = res.data;
      
      if (data.success) {
        setPreviewHtml(data.html || '');
        setPreviewInfo(data.info);
      } else {
        setError(data.error || 'Ошибка генерации превью');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Автообновление при изменении прозрачности
  useEffect(() => {
    if (selectedMode === 'dynamic' && selectedPresetId) {
      generatePreview(selectedPresetId);
    } else if (selectedMode === 'static' && selectedCuratedId) {
      generatePreview(undefined, selectedCuratedId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayOpacity]);

  const previewSrcDoc = useMemo(() => (previewHtml ? buildBackgroundPreviewDocument(previewHtml) : ''), [previewHtml]);

  // Полноэкранный просмотр: новое окно без opener, фон — в sandboxed iframe
  const openFullscreen = () => {
    if (!previewHtml) return;
    const previewWindow = openSandboxedPreviewWindow('Background Preview');
    if (!previewWindow) {
      setError('Браузер заблокировал новое окно. Разрешите всплывающие окна для этого сайта.');
      return;
    }
    const label = String(previewInfo?.subtype || previewInfo?.type || 'Unknown');
    previewWindow.render(buildBackgroundFullscreenDocument(previewHtml, label));
  };

  // Фильтрация
  const filteredPresets = presets.filter(p => {
    if (p.type !== 'dynamic') return false;
    if (performanceFilter && p.performance_weight > performanceFilter) return false;
    if (colorScheme !== 'any' && p.color_scheme !== 'any' && p.color_scheme !== colorScheme) return false;
    return true;
  });

  const filteredCurated = curatedBackgrounds.filter(c => {
    if (colorScheme !== 'any' && c.color_scheme !== colorScheme) return false;
    return true;
  });

  // Группировка пресетов
  const groupedPresets = filteredPresets.reduce((acc, preset) => {
    const key = preset.subtype;
    if (!acc[key]) acc[key] = [];
    acc[key].push(preset);
    return acc;
  }, {} as Record<string, BackgroundPreset[]>);

  if (loading) {
    return (
      <div className="bg-tester">

        <div className="bg-tester__loading">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="bg-tester">
      <div className="u2-card"><div className="u2-cardInner"><h2 className="u2-title">Превью динамических фонов</h2><p className="u2-muted">Обновление миниатюр библиотеки фонов генератора.</p><button type="button" className="u2-btn is-secondary" disabled={regenerating} onClick={regenerateThumbnails}>{regenerating ? 'Обновление…' : 'Обновить превью фонов'}</button>{thumbnailNotice && <p role="status">{thumbnailNotice}</p>}</div></div>
      {/* Заголовок */}
      <div className="bg-tester__header">
        <h1>Тестер фонов</h1>
        <p>Тестирование глобальных фонов</p>
      </div>

      <div className="bg-tester__body">
        {/* Левая панель - настройки */}
        <div className="bg-tester__sidebar">
          {/* Режим */}
          <div className="bg-tester__section">
            <h3>Режим</h3>
            <div className="bg-tester__mode-toggle">
              <button 
                type="button"
                className={selectedMode === 'dynamic' ? 'active' : ''}
                onClick={() => setSelectedMode('dynamic')}
              >
                Динамический
              </button>
              <button 
                type="button"
                className={selectedMode === 'static' ? 'active' : ''}
                onClick={() => setSelectedMode('static')}
              >
                Статический
              </button>
            </div>
          </div>

          {/* Фильтры */}
          <div className="bg-tester__section">
            <h3>Фильтры</h3>
            
            <label>Цветовая схема:</label>
            <select value={colorScheme} onChange={e => setColorScheme(e.target.value)}>
              <option value="any">Любая</option>
              <option value="light">Светлая</option>
              <option value="dark">Тёмная</option>
              <option value="neutral">Нейтральная</option>
            </select>
            
            {selectedMode === 'dynamic' && (
              <>
                <label>Макс. нагрузка:</label>
                <select value={performanceFilter} onChange={e => setPerformanceFilter(Number(e.target.value))}>
                  <option value={1}>Лёгкие</option>
                  <option value={2}>Средние</option>
                  <option value={3}>Все</option>
                </select>
              </>
            )}
            
            <label>Прозрачность фона: {(overlayOpacity * 100).toFixed(0)}%</label>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={overlayOpacity}
              onChange={(e) => setOverlayOpacity(Number(e.target.value))}
            />
            <small>0% = картинка видна полностью, 100% = белый фон</small>
          </div>

          {/* Список пресетов/изображений */}
          <div className="bg-tester__section bg-tester__list">
            {selectedMode === 'dynamic' ? (
              <>
                <h3>Пресеты ({filteredPresets.length})</h3>
                {Object.entries(groupedPresets).map(([subtype, items]) => (
                  <div key={subtype} className="bg-tester__group">
                    <h4>{subtype}</h4>
                    {items.map(preset => (
                      <div 
                        key={preset.id}
                        className={`bg-tester__item ${selectedPresetId === preset.id ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedPresetId(preset.id);
                          generatePreview(preset.id);
                        }}
                      >
                        <span>{preset.name}</span>
                        <span className="weight">Вес: {preset.performance_weight}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </>
            ) : (
              <>
                <h3>Изображения ({filteredCurated.length})</h3>
                <div className="bg-tester__images">
                  {filteredCurated.map(bg => (
                    <div 
                      key={bg.id}
                      className={`bg-tester__thumb ${selectedCuratedId === bg.id ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedCuratedId(bg.id);
                        generatePreview(undefined, bg.id);
                      }}
                      style={{ backgroundImage: `url(${bg.url})` }}
                      title={bg.name}
                    >
                      {bg.seamless && <span className="badge">seamless</span>}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Кнопки */}
          <div className="bg-tester__section">
            <button 
              type="button"
              className="bg-tester__btn bg-tester__btn--primary"
              onClick={() => generatePreview(selectedPresetId || undefined, selectedCuratedId || undefined)}
              disabled={previewLoading}
            >
              {previewLoading ? 'Генерация…' : 'Обновить'}
            </button>
            <button 
              type="button"
              className="bg-tester__btn"
              onClick={() => {
                if (selectedMode === 'dynamic' && filteredPresets.length > 0) {
                  const random = filteredPresets[Math.floor(Math.random() * filteredPresets.length)];
                  setSelectedPresetId(random.id);
                  generatePreview(random.id);
                } else if (selectedMode === 'static' && filteredCurated.length > 0) {
                  const random = filteredCurated[Math.floor(Math.random() * filteredCurated.length)];
                  setSelectedCuratedId(random.id);
                  generatePreview(undefined, random.id);
                }
              }}
            >
              Случайный
            </button>
          </div>
        </div>

        {/* Правая панель - превью */}
        <div className="bg-tester__preview">
          {error && (
            <div className="bg-tester__error">
              {error}
              <button type="button" onClick={() => setError(null)} aria-label="Закрыть">
                ×
              </button>
            </div>
          )}

          {/* Область превью - ЧИСТАЯ структура */}
          <div className="bg-tester__preview-area">
            {previewHtml ? (
              <iframe
                className="bg-tester__preview-frame bg-tester__preview-frame--sandboxed"
                title="Превью фона"
                sandbox={PREVIEW_SANDBOX}
                srcDoc={previewSrcDoc}
                tabIndex={-1}
              />
            ) : (
              <div className="bg-tester__placeholder">
                Выберите изображение или пресет
              </div>
            )}
            
            {/* Пример контента поверх фона */}
            {previewHtml && (
              <div className="bg-tester__sample-content">
                <div className="bg-tester__sample-card">
                  <h2>Пример контента</h2>
                  <p>Так будет выглядеть текст поверх фона.</p>
                  <div className="bg-tester__sample-buttons">
                    <span>Карточка 1</span>
                    <span>Карточка 2</span>
                    <span>Карточка 3</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Инфо панель */}
          {previewInfo && (
            <div className="bg-tester__info">
              <span><strong>Тип:</strong> {previewInfo.type}</span>
              <span><strong>Подтип:</strong> {previewInfo.subtype}</span>
              <button type="button" onClick={openFullscreen}>
                Полный экран
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BackgroundTester;
